import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  useOvertimeRequestDetailQuery,
  useUpdateOvertimeRequestMutation,
} from "../api/queries";
import { OTRequestForm } from "../ui/OTRequestForm";
import type { OTRequestFormValues } from "../ui/OTRequestForm";
import { Badge, Button, toast } from "@devhop/ui";
import { ArrowLeft, HelpCircle, Loader2 } from "lucide-react";
import { OTRequestDetailTour, useOTRequestDetailTour } from "../tour/OTRequestDetailTour";

const STATUS_LABEL: Record<string, string> = {
  pending: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "secondary",
  approved: "default",
  rejected: "destructive",
};

const REQUESTS_LIST_SEARCH = {
  offset: 0,
  limit: 20,
  status: undefined as string | undefined,
  search: undefined as string | undefined,
};

type DetailSearch = {
  from?: string;
  offset?: number;
  limit?: number;
  periodYear?: number;
  periodMonth?: number;
  status?: string;
  search?: string;
  employeeId?: string;
};

export function OTRequestDetailPage() {
  const { id } = useParams({ strict: false });
  const nav = useNavigate();
  const detailSearch = useSearch({ strict: false }) as DetailSearch | undefined;
  const { data: request, isLoading, error } = useOvertimeRequestDetailQuery(id);
  const updateMutation = useUpdateOvertimeRequestMutation();
  const tour = useOTRequestDetailTour();

  const fromApprovals = detailSearch?.from === "approvals";
  const backToApprovalsSearch = fromApprovals
    ? {
        offset: detailSearch?.offset ?? 0,
        limit: detailSearch?.limit ?? 20,
        periodYear: detailSearch?.periodYear,
        periodMonth: detailSearch?.periodMonth,
        status: detailSearch?.status,
        search: detailSearch?.search,
        employeeId: detailSearch?.employeeId,
      }
    : null;

  const goBack = () => {
    if (backToApprovalsSearch) {
      nav({ to: "/app/hr/overtime/approvals", search: backToApprovalsSearch });
    } else {
      nav({ to: "/app/hr/overtime/requests", search: REQUESTS_LIST_SEARCH });
    }
  };

  const mode = request?.status === "pending" ? "edit" : "view";

  const handleSubmit = (values: OTRequestFormValues) => {
    if (!id || mode !== "edit") return;
    updateMutation.mutate(
      {
        id,
        dto: {
          workDate: values.workDate,
          startTime: values.startTime,
          endTime: values.endTime,
          reason: values.reason.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success("ບັນທຶກການແກ້ໄຂສຳເລັດ");
          if (backToApprovalsSearch) {
            nav({ to: "/app/hr/overtime/approvals", search: backToApprovalsSearch });
          } else {
            nav({ to: "/app/hr/overtime/requests", search: REQUESTS_LIST_SEARCH });
          }
        },
        onError: (err: { message?: string; status?: number }) => {
          const msg = err?.message ?? "";
          if (msg.includes("Duplicate") || err?.status === 409) {
            toast.error("ທ່ານມີຄຳຂໍ OT ສຳລັບວັນນີ້ແລ້ວ ບໍ່ສາມາດສົ່ງຊ້ຳໄດ້");
            return;
          }
          toast.error(msg || "ເກີດຂໍ້ຜິດພາດ");
        },
      },
    );
  };

  if (isLoading || (!request && !error)) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </Main>
      </>
    );
  }

  if (error || !request) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto w-full max-w-4xl py-4">
            <p className="text-destructive">ບໍ່ພົບຄຳຂໍ OT ຫຼືບໍ່ມີສິດເຂົ້າເຖິງ.</p>
            <Button variant="outline" className="mt-4" onClick={goBack}>
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <OTRequestDetailTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div
          className="mb-6 flex items-center justify-between gap-2"
          data-tourid="ot-request-detail-back"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ລາຍລະອຽດຄຳຂໍ OT</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="flex items-center gap-2 text-muted-foreground text-sm">
              {request.workDate}
              <Badge variant={STATUS_VARIANT[request.status] ?? "outline"}>
                {STATUS_LABEL[request.status] ?? request.status}
              </Badge>
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={goBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            ກັບໄປລາຍການ
          </Button>
        </div>

        <div className="mx-auto w-full max-w-4xl space-y-6 py-4">
          <div data-tourid="ot-request-detail-form">
          <OTRequestForm
            mode={mode}
            request={request}
            onSubmit={handleSubmit}
            onCancel={goBack}
            isSubmitting={updateMutation.isPending}
          />
          </div>
        </div>
      </Main>
    </>
  );
}
